import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ASPECT_RATIO = 16 / 9;

function getCroppedCanvas(image, crop) {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0, 0,
    canvas.width,
    canvas.height,
  );
  return canvas;
}

const ImageCropper = ({ value, onChange, label = 'Thumbnail' }) => {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [urlInput, setUrlInput] = useState(value || '');
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result);
      setShowCropper(true);
      setCrop(null);
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const cropWidth = width * 0.9;
    const cropHeight = cropWidth / ASPECT_RATIO;
    const finalHeight = Math.min(cropHeight, height * 0.9);
    const finalWidth = finalHeight * ASPECT_RATIO;
    const x = (width - finalWidth) / 2;
    const y = (height - finalHeight) / 2;
    const initialCrop = { unit: 'px', x, y, width: finalWidth, height: finalHeight };
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  }, []);

  const handleCropDone = () => {
    if (!imgRef.current || !completedCrop) return;
    const canvas = getCroppedCanvas(imgRef.current, completedCrop);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    onChange(dataUrl);
    setUrlInput('');
    setShowCropper(false);
    setImgSrc('');
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setImgSrc('');
    setCrop(null);
    setCompletedCrop(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUrlSubmit = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    setImgSrc(trimmed);
    setShowCropper(true);
    setCrop(null);
    setCompletedCrop(null);
  };

  const handleRemove = () => {
    onChange('');
    setUrlInput('');
    setImgSrc('');
    setShowCropper(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-dark-700">{label}</label>

      {/* Current thumbnail preview */}
      {value && !showCropper && (
        <div className="relative rounded-lg overflow-hidden border border-dark-200 group">
          <img src={value} alt="Thumbnail" className="w-full h-48 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button type="button" onClick={() => fileInputRef.current?.click()} className="bg-white text-dark-800 text-sm font-medium px-4 py-2 rounded-lg hover:bg-dark-50">
              Replace
            </button>
            <button type="button" onClick={handleRemove} className="bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-red-600">
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Upload / URL options when no thumbnail */}
      {!value && !showCropper && (
        <div className="border-2 border-dashed border-dark-200 rounded-lg p-6 text-center space-y-4">
          <div>
            <svg className="w-10 h-10 text-dark-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-dark-500">Upload an image or paste a URL</p>
            <p className="text-xs text-dark-400 mt-0.5">Image will be cropped to 16:9 aspect ratio</p>
          </div>

          <button type="button" onClick={() => fileInputRef.current?.click()}
            className="btn-primary px-5 py-2 text-sm font-medium rounded-lg">
            Choose Image
          </button>

          <div className="flex items-center gap-2 text-xs text-dark-400">
            <div className="flex-1 h-px bg-dark-200" />
            <span>or paste URL</span>
            <div className="flex-1 h-px bg-dark-200" />
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleUrlSubmit(); } }}
            />
            <button type="button" onClick={handleUrlSubmit} disabled={!urlInput.trim()}
              className="px-4 py-2 border border-primary-300 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed">
              Load
            </button>
          </div>
        </div>
      )}

      {/* Cropper modal */}
      {showCropper && imgSrc && (
        <div className="border border-dark-200 rounded-lg overflow-hidden bg-dark-50">
          <div className="px-4 py-3 bg-dark-100 flex items-center justify-between">
            <p className="text-sm font-medium text-dark-700">Crop Thumbnail (16:9)</p>
            <div className="flex gap-2">
              <button type="button" onClick={handleCancelCrop}
                className="px-3 py-1.5 text-sm border border-dark-200 rounded-lg hover:bg-dark-50">
                Cancel
              </button>
              <button type="button" onClick={handleCropDone} disabled={!completedCrop}
                className="btn-primary px-4 py-1.5 text-sm rounded-lg disabled:opacity-40">
                Apply Crop
              </button>
            </div>
          </div>
          <div className="p-4 flex justify-center" style={{ maxHeight: '400px', overflow: 'auto' }}>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={ASPECT_RATIO}
            >
              <img
                src={imgSrc}
                alt="Crop source"
                onLoad={onImageLoad}
                style={{ maxHeight: '360px' }}
                crossOrigin="anonymous"
              />
            </ReactCrop>
          </div>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" onChange={onSelectFile} className="hidden" />
    </div>
  );
};

export default ImageCropper;
